"use client";

import { createStore, useStore } from "zustand";
import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
} from "react";

// Global recorder quality settings - easily configurable
export const RECORDER_CONFIG = {
  // MediaRecorder settings
  recorder: {
    mimeType: "video/webm",
    videoBitsPerSecond: 800000, // 800 kbps
    audioBitsPerSecond: 64000, // 64 kbps
    firstChunkTimeSlice: 2_000,
    timeSlice: 10_000, // Request data every 10s second
  },
  // Audio context settings
  audioContext: {
    sampleRate: 44100,
  },
} as const;

// Helper function to get supported mime type with fallback
export function getSupportedMimeType(): string {
  const types = [
    RECORDER_CONFIG.recorder.mimeType,
    "video/webm; codecs=vp8,opus",
    "video/webm; codecs=h264,opus",
    "video/webm",
    "video/mp4",
  ];

  return types.find((type) => MediaRecorder.isTypeSupported(type)) || types[0];
}

// Helper function to get recorder options
export function getRecorderOptions(): MediaRecorderOptions {
  return {
    mimeType: getSupportedMimeType(),
    videoBitsPerSecond: RECORDER_CONFIG.recorder.videoBitsPerSecond,
    audioBitsPerSecond: RECORDER_CONFIG.recorder.audioBitsPerSecond,
  };
}

type RecorderState = "idle" | "recording" | "paused" | "error";

interface RecorderError {
  message: string;
  code?: string;
  timestamp: number;
}

interface RecorderStore {
  // State
  state: RecorderState;
  error: RecorderError | null;
  recordingStream: MediaStream | null;
  userStream: MediaStream | null;
  agentStream: MediaStream | null;
  lastChunk: Blob | null;

  // Internal refs (not for external use)
  mediaRecorder: MediaRecorder | null;
  audioContext: AudioContext | null;
}

export const recorderStore = createStore<RecorderStore>(() => ({
  // Initial state
  state: "idle",
  error: null,
  recordingStream: null,
  userStream: null,
  agentStream: null,
  lastChunk: null,
  mediaRecorder: null,
  audioContext: null,
}));

// Optimized selectors
export const useRecorderState = () =>
  useStore(recorderStore, (state) => state.state);
export const useRecorderError = () =>
  useStore(recorderStore, (state) => state.error);
export const useRecordingStream = () =>
  useStore(recorderStore, (state) => state.recordingStream);

// Context for recorder functions
interface RecorderContextValue {
  setUserStream: (stream: MediaStream | null) => void;
  setAgentStream: (stream: MediaStream | null) => void;
  setupCombinedStream: () => void;
  handleError: (err: Error | string, code?: string) => void;
  start: (onChunk?: (chunk: Blob) => void) => void;
  stop: () => void;
  pause: () => void;
  resume: () => void;
  finish: () => Promise<Blob | null>;
  cleanup: () => void;
}

const RecorderContext = createContext<RecorderContextValue | null>(null);

export const RecorderProvider = ({ children }: { children: ReactNode }) => {
  const value = useMemo<RecorderContextValue>(() => {
    const setUserStream = (stream: MediaStream | null) => {
      recorderStore.setState({ userStream: stream });
      setupCombinedStream();
    };

    const setAgentStream = (stream: MediaStream | null) => {
      recorderStore.setState({ agentStream: stream });
      setupCombinedStream();
    };

    const setupCombinedStream = () => {
      const { userStream, agentStream, audioContext } =
        recorderStore.getState();

      // Cleanup existing audio context
      if (audioContext?.state !== "closed") {
        audioContext?.close();
      }

      if (!userStream || !agentStream) {
        recorderStore.setState({ recordingStream: null, audioContext: null });
        return;
      }

      try {
        const ctx = new AudioContext({
          sampleRate: RECORDER_CONFIG.audioContext.sampleRate,
        });

        const dest = ctx.createMediaStreamDestination();

        // Mix user and agent audio
        const userAudio = ctx.createMediaStreamSource(userStream);
        const agentAudio = ctx.createMediaStreamSource(agentStream);

        userAudio.connect(dest);
        agentAudio.connect(dest);

        // Combine mixed audio with user video
        const combinedStream = new MediaStream([
          ...dest.stream.getAudioTracks(),
          ...userStream.getVideoTracks(),
        ]);

        recorderStore.setState({
          recordingStream: combinedStream,
          audioContext: ctx,
        });
      } catch (err) {
        handleError(err as Error, "STREAM_SETUP_ERROR");
      }
    };

    const handleError = (err: Error | string, code?: string) => {
      const errorMessage = err instanceof Error ? err.message : err;
      const errorObj: RecorderError = {
        message: errorMessage,
        code,
        timestamp: Date.now(),
      };
      recorderStore.setState({ error: errorObj, state: "error" });
      console.error("Recorder error:", errorObj);
    };

    const start = async (onChunk?: (chunk: Blob) => void) => {
      const store = recorderStore.getState();
      const { recordingStream, state } = store;

      if (!recordingStream) {
        handleError("No stream available to record", "NO_STREAM");
        return;
      }

      if (state === "recording") {
        console.warn("Recording already in progress");
        return;
      }

      try {
        const recorderOptions = getRecorderOptions();
        recorderStore.setState({ lastChunk: null });
        const recorder = new MediaRecorder(recordingStream, recorderOptions);

        recorder.addEventListener("dataavailable", (event) => {
          if (event.data.size > 0 && onChunk) {
            onChunk(event.data);
          }
          if (event.data.size > 0) {
            recorderStore.setState({ lastChunk: event.data });
          }
        });

        type FirstChunkState =
          | "idle"
          | "start-fired"
          | "timer-fired"
          | "requested";
        let firstChunkState: FirstChunkState = "idle";

        const setFirstChunkState = (next: FirstChunkState) => {
          if (firstChunkState === next) return;
          const allowedTransitions: Record<FirstChunkState, FirstChunkState[]> = {
            "idle": ["start-fired", "timer-fired"],
            "start-fired": ["requested"],
            "timer-fired": ["requested"],
            "requested": [],
          };
          if (!allowedTransitions[firstChunkState].includes(next)) return;
          firstChunkState = next;
        };

        const maybeRequestFirstChunk = () => {
          if (firstChunkState === "requested") return;
          if (recorder.state !== "recording") return;
          setFirstChunkState("requested");
          recorder.requestData();
        };

        const handleFirstChunkEvent = (event: "start" | "timer") => {
          if (firstChunkState === "requested") return;

          if (event === "start") {
            if (firstChunkState === "timer-fired") {
              maybeRequestFirstChunk();
            } else {
              setFirstChunkState("start-fired");
            }
            return;
          }

          if (firstChunkState === "start-fired") {
            maybeRequestFirstChunk();
          } else {
            setFirstChunkState("timer-fired");
          }
        };

        recorder.addEventListener("start", () => {
          console.log("Recording started with config:", recorderOptions);
          recorderStore.setState({ state: "recording", error: null });
          handleFirstChunkEvent("start");
        });

        recorder.addEventListener("error", (event) => {
          handleError(
            (event as ErrorEvent).error || "MediaRecorder error",
            "MEDIARECORDER_ERROR",
          );
        });

        recorder.addEventListener("stop", () => {
          const currentState = recorderStore.getState().state;
          if (currentState !== "error") {
            recorderStore.setState({ state: "idle" });
          }
        });

        recorderStore.setState({ mediaRecorder: recorder });
        recorder.start(RECORDER_CONFIG.recorder.timeSlice);

        setTimeout(() => {
          handleFirstChunkEvent("timer");
        }, RECORDER_CONFIG.recorder.firstChunkTimeSlice);
      } catch (err) {
        handleError(err as Error, "START_ERROR");
      }
    };

    const stop = () => {
      const { mediaRecorder } = recorderStore.getState();

      if (!mediaRecorder) {
        console.warn("No recorder to stop");
        return;
      }

      if (
        mediaRecorder.state === "recording" ||
        mediaRecorder.state === "paused"
      ) {
        mediaRecorder.stop();
      }
    };

    const pause = () => {
      const { mediaRecorder } = recorderStore.getState();

      if (!mediaRecorder) {
        console.warn("No recorder to pause");
        return;
      }

      if (mediaRecorder.state === "recording") {
        try {
          mediaRecorder.pause();
          recorderStore.setState({ state: "paused" });
        } catch (err) {
          handleError(err as Error, "PAUSE_ERROR");
        }
      }
    };

    const resume = () => {
      const { mediaRecorder } = recorderStore.getState();

      if (!mediaRecorder) {
        console.warn("No recorder to resume");
        return;
      }

      if (mediaRecorder.state === "paused") {
        try {
          mediaRecorder.resume();
          recorderStore.setState({ state: "recording" });
        } catch (err) {
          handleError(err as Error, "RESUME_ERROR");
        }
      }
    };

    const waitForRecorderStart = async (recorder: MediaRecorder) => {
      if (recorder.state !== "inactive") return;
      await new Promise<void>((resolve) => {
        let resolved = false;
        const handleStart = () => {
          if (resolved) return;
          resolved = true;
          recorder.removeEventListener("start", handleStart);
          resolve();
        };
        recorder.addEventListener("start", handleStart);
        setTimeout(() => {
          if (resolved) return;
          resolved = true;
          recorder.removeEventListener("start", handleStart);
          resolve();
        }, 1500);
      });
    };

    const finish = async (): Promise<Blob | null> => {
      const { mediaRecorder, audioContext } = recorderStore.getState();

      if (!mediaRecorder) {
        console.warn("No recorder to finish");
        return Promise.resolve(null);
      }

      await waitForRecorderStart(mediaRecorder);

      return new Promise((resolve) => {
        let finalChunk: Blob | null = null;

        const handleDataAvailable = (event: BlobEvent) => {
          if (event.data.size > 0) {
            finalChunk = event.data;
          }
        };

        const isInactive = (recorder: MediaRecorder) =>
          recorder.state === "inactive";
        let cleanedUp = false;
        let resolved = false;
        const cleanupListeners = () => {
          if (cleanedUp) {
            return;
          }
          cleanedUp = true;
          mediaRecorder.removeEventListener(
            "dataavailable",
            handleDataAvailable,
          );
          mediaRecorder.removeEventListener("stop", handleStop);
          audioContext?.close();
        };

        const resolveOnce = () => {
          if (resolved) return;
          resolved = true;
          cleanupListeners();
          if (finalChunk) {
            resolve(finalChunk);
            return;
          }
          const fallback = recorderStore.getState().lastChunk;
          resolve(fallback ?? null);
        };

        const handleStop = () => {
          resolveOnce();
        };

        mediaRecorder.addEventListener("dataavailable", handleDataAvailable);
        mediaRecorder.addEventListener("stop", handleStop);

        if (mediaRecorder.state !== "inactive") {
          try {
            mediaRecorder.requestData();
          } catch (error) {
            handleError(error as Error, "REQUEST_DATA_ERROR");
          }
          try {
            mediaRecorder.stop();
          } catch (error) {
            handleError(error as Error, "STOP_ERROR");
          }

          if (isInactive(mediaRecorder)) {
            resolveOnce();
          }
        } else {
          resolveOnce();
        }
      });
    };

    const cleanup = () => {
      const { mediaRecorder, audioContext } = recorderStore.getState();

      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
      if (audioContext?.state !== "closed") {
        audioContext?.close();
      }

      recorderStore.setState({
        mediaRecorder: null,
        audioContext: null,
        recordingStream: null,
        lastChunk: null,
      });
    };

    return {
      setUserStream,
      setAgentStream,
      setupCombinedStream,
      handleError,
      start,
      stop,
      pause,
      resume,
      finish,
      cleanup,
    };
  }, []);

  return (
    <RecorderContext.Provider value={value}>
      {children}
    </RecorderContext.Provider>
  );
};

export const useRecorderContext = () => {
  const context = useContext(RecorderContext);
  if (!context) {
    throw new Error("useRecorderContext must be used within RecorderProvider");
  }
  return context;
};

// Hook for convenience
export const useRecorder = (options: {
  onChunk?: (chunk: Blob) => void;
}) => {
  const { onChunk } = options;
  const { start, stop, pause, resume, finish, setUserStream, setAgentStream } =
    useRecorderContext();
  const state = useStore(recorderStore, (s) => s.state);

  const wrappedStart = (args: {
    userStream: MediaStream | null;
    agentStream: MediaStream | null;
  }) => {
    setUserStream(args.userStream);
    setAgentStream(args.agentStream);

    start(onChunk);
  };

  return {
    finish,
    start: wrappedStart,
    stop,
    pause,
    resume,
    state,
  };
};
